/**
 * useWorkflowExecutions - Observabilidade do módulo Workflow 2.0
 * -----------------------------------------------------------------------------
 * Lê as RPCs seguras:
 *   - `get_workflow_execution_log`    → KPIs + lista paginada das execuções
 *   - `get_workflow_execution_detail` → linha do tempo de uma execução
 *
 * Toda leitura passa pelo banco (nunca SELECT direto no frontend) e o tenant é
 * enviado apenas como DICA (`p_tenant_hint`), sendo revalidado pela RPC.
 *
 * @version 1.1.0
 * RELEASE: 2026-09-06 v1.1.0 - Passos passam a expor `input`/`output` (dados de
 *          entrada/saída de cada bloco, exibidos no modal de execuções).
 * RELEASE: 2026-09-06 v1.0.0 - Criação inicial (painel de execuções e KPIs).
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useWorkflowExecutions');

/** Indicadores agregados das execuções */
export interface WorkflowExecutionKpis {
  total: number;
  em_andamento: number;
  concluidas: number;
  com_erro: number;
  hoje: number;
}

/** Linha da lista de execuções */
export interface WorkflowExecutionItem {
  id: string;
  definition_id: string;
  definition_name: string;
  root_entity_type: string;
  root_entity_id: string | null;
  status: string;
  current_node_label: string | null;
  error_summary: string | null;
  needs_review: boolean | null;
  started_at: string | null;
  last_event_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  duracao_segundos: number | null;
  /** Versão do fluxo em que a execução rodou (RPC v1.1.0) */
  version_number?: number | null;
  /** Versão publicada vigente do fluxo */
  current_version_number?: number | null;
}

/** Payload completo da listagem */
export interface WorkflowExecutionLog {
  kpis: WorkflowExecutionKpis;
  total: number;
  page: number;
  page_size: number;
  instances: WorkflowExecutionItem[];
}

/** Bloco executado dentro de uma instância */
export interface WorkflowExecutionStep {
  id: string;
  node_id: string | null;
  node_label: string | null;
  node_type: string | null;
  status: string;
  attempt: number | null;
  error_message: string | null;
  /** Dados de entrada do bloco (JSON gravado pelo motor) */
  input: unknown | null;
  /** Dados de saída do bloco (JSON gravado pelo motor) */
  output: unknown | null;
  scheduled_for: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string | null;
}

/** Payload do detalhamento de uma execução */
export interface WorkflowExecutionDetail {
  instance: WorkflowExecutionItem & { review_reason?: string | null };
  steps: WorkflowExecutionStep[];
}

/** Filtros aceitos pela listagem */
export interface WorkflowExecutionFilters {
  /** Limita a um processo específico (null = todos do tenant) */
  definitionId?: string | null;
  /** todos | em_andamento | concluidas | com_erro | canceladas */
  status?: string;
  /** todos | hoje | 7d | 30d */
  period?: string;
  /** Busca pelo código do registro de origem ou da execução */
  search?: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}

/** Fuso usado para o KPI "hoje" (padrão do sistema) */
const TIMEZONE = 'America/Sao_Paulo';

/**
 * Lista paginada + indicadores das execuções de workflow.
 */
export function useWorkflowExecutionLog({
  definitionId = null,
  status = 'todos',
  period = 'todos',
  search = '',
  page = 1,
  pageSize = 25,
  enabled = true,
}: WorkflowExecutionFilters = {}) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  const query = useQuery({
    queryKey: ['workflow-execution-log', tenantId, definitionId, status, period, search, page, pageSize],
    enabled: !!tenantId && enabled,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('get_workflow_execution_log', {
        p_definition_id: definitionId,
        p_status: status,
        p_period: period,
        p_search: search?.trim() || null,
        p_page: page,
        p_page_size: pageSize,
        p_timezone: TIMEZONE,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Falha ao carregar execuções do workflow', { error: error.message });
        throw error;
      }
      return data as WorkflowExecutionLog;
    },
  });

  return {
    kpis: query.data?.kpis ?? { total: 0, em_andamento: 0, concluidas: 0, com_erro: 0, hoje: 0 },
    instances: query.data?.instances ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}

/**
 * Detalhamento (linha do tempo) de uma execução específica.
 */
export function useWorkflowExecutionDetail(instanceId: string | null) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  const query = useQuery({
    queryKey: ['workflow-execution-detail', tenantId, instanceId],
    enabled: !!tenantId && !!instanceId,
    staleTime: 10_000,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('get_workflow_execution_detail', {
        p_instance_id: instanceId,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Falha ao carregar detalhe da execução', { error: error.message });
        throw error;
      }
      return data as WorkflowExecutionDetail;
    },
  });

  return {
    detalhe: query.data ?? null,
    isLoading: query.isLoading,
  };
}
